import { MerchantApi } from '@/api/merchantApi'

//utils
import { getDeliveryCells, getPickupCells, setRowCheckingState, setPriceSettings, setRowExpandedState, setDeliveryRowBySearch } from './price-settings-utils'

import { Module } from 'vuex';
import { PriceSettingsState, DeliveryPriceSettings, PriceSettings, PickupPriceSettings, Discount } from '@/types/price-settings'

import { CellSettings } from '@/types/table'

import store from '@/store/price-settings';

const commonCellSetting: CellSettings<PriceSettings> = {
    Price: { name: 'Цена, Р' },
    Discounts: { name: 'Скидки за объем' }
}

const deliveryCellSettings: CellSettings<PriceSettings> = {
    Name: { name: 'Регион с доставкой' },
    MinVolume: { name: 'Минимальный объем доставки' },
    MaxVolume: { name: 'Максимальный объем доставки' },
    MinTerm: { name: 'Минимальный срок доставки', tooltip: 'Указывается количество календарных дней' },
    MaxTerm: { name: 'Максимальный срок доставки', tooltip: 'Указывается количество календарных дней' },
    ...commonCellSetting
}

const pickupCellSettings: CellSettings<PriceSettings> = {
    Name: { name: 'Склад' },
    MinVolume: { name: 'Минимальный объем отгрузки' },
    MaxVolume: { name: 'Максимальный объем отгрузки' },
    MinTerm: { name: 'Минимальный срок отгрузки', tooltip: 'Указывается количество календарных дней' },
    MaxTerm: { name: 'Максимальный срок отгрузки', tooltip: 'Указывается количество календарных дней' },
    ...commonCellSetting
}


interface CheckModel {
    row: PriceSettings;
    inputValue: boolean;
    typeCode: string;
}

interface ToggleModel {
    row: PriceSettings;
    typeCode: string;
}

interface DiscountUpdateModel {
    rowId: number | number;
    discountId: number;
    typeCode: string;
}

export const priceSettings: Module<PriceSettingsState, RootStatePriceSettings> = {
    state: {
        deliveryPriceSettings: null,
        pickupPriceSettings: null,
        unitName: '',
        countryName: '',
        keyword: ''
    },
    actions: {
        /**
         * Получение списка адресов доставки
         * @param context 
         */
        async getDeliveryPriceSettings({ commit }, productId: number) {
            try {
                const response = await MerchantApi.getDeliveryPriceSettings(productId);

                if(response.data.Items) {
                    commit('setDeliveryPriceSettings', response.data.Items);
                }
            }
            catch(error) {
                console.error(error, 'Произошла ошибка при попытке получить список скидок для доставки');
            }
        },
        async getPickupPriceSettings({ commit }, productId: number) {
            try {
                const response = await MerchantApi.getPickupPriceSettings(productId);

                if(response.data.Items) {
                    commit('setPickupPriceSettings', response.data.Items);
                }
            }
            catch(error) {
                console.error(error, 'Произошла ошибка при попытке получить список скидок для самовывоза');
            }
        },
        async saveDeliveryPriceSettings({ commit }, productId: number) {
            try {
                if(store.state.priceSettings.deliveryPriceSettings) {
                    return await MerchantApi.updateDeliveryPriceSettings(store.state.priceSettings.deliveryPriceSettings, productId);
                }
                else {
                    console.error('Отсутствуют даные для отправки');
                }
            }
            catch(error) {
                console.error(error, 'Произошла ошибка при сохранить данные скидок для доставки');
                return false
            }
        },
        async savePickupPriceSettings({ commit }, productId: number) {
            try {
                if(store.state.priceSettings.pickupPriceSettings) {
                    return await MerchantApi.updatePickupPriceSettings(store.state.priceSettings.pickupPriceSettings, productId);
                }
                else {
                    console.error('Отсутствуют даные для отправки');
                }
            }
            catch(error) {
                console.error(error, 'Произошла ошибка при сохранить данные скидок для доставки');
                return false
            }
        },
        searchDelivery({ commit }, keyword: string): void {
            commit('setDeliveryRowBySearch', keyword);
        },
        searchPickup({ commit }, keyword: string): void {
            commit('setPickupRowBySearch', keyword);
        },
        check({ commit }, data: CheckModel): void {
            commit('setRowCheckingState', data);
        },
        toggle({ commit }, data: ToggleModel) {
            commit('setRowExpandedState', data);
        },
        addEmptyDiscountRow({ commit }, data: DiscountUpdateModel) {
            commit('addEmptyDiscountRow', data);
        },
        deleteDiscount({ commit }, data: DiscountUpdateModel) {
            commit('deleteDiscount', data);
        },
        deleteDiscountList({ commit }, data: PriceSettings) {
            commit('deleteDiscountList', data);
        },
        setUnitName({ commit }, unitName: string) {
            commit('setUnitName', unitName);
        }
    },
    mutations: {
        setDeliveryRowBySearch(state: PriceSettingsState, keyword: string) {

            state.keyword = keyword;

            if(state.deliveryPriceSettings) {
                setDeliveryRowBySearch(state.deliveryPriceSettings, keyword)
            }
        },
        setPickupRowBySearch(state: PriceSettingsState, keyword: string) {

            state.keyword = keyword;

            if(state.pickupPriceSettings) {
                setDeliveryRowBySearch(state.pickupPriceSettings, keyword)
            }
        },
        deleteDiscount(state: PriceSettingsState, data: DiscountUpdateModel) {
            const rows = data.typeCode == 'delivery' ? state.deliveryPriceSettings : state.pickupPriceSettings;

            rows?.forEach((item) => {
                if(item.Id === data.rowId) {
                    item.Discounts = item.Discounts.filter((discount) => discount.Id != data.discountId)
                }
            });
        },
        addEmptyDiscountRow(state: PriceSettingsState, data: DiscountUpdateModel) {
            
            const rows = data.typeCode == 'delivery' ? state.deliveryPriceSettings : state.pickupPriceSettings;
            const row = rows?.find((item) => item.Id === data.rowId);

            if(row) {
                const Id = 0;
                const discounts = row.Discounts;
                const lastDiscount = discounts[discounts.length - 1]

                if(lastDiscount.MaxVolume || lastDiscount.MaxVolume === 0 || lastDiscount.MinVolume || lastDiscount.MinVolume === 0 || lastDiscount.Percent || lastDiscount.Percent === 0) {
                    const discount: Discount = {
                        Id: row.Discounts.length ? row?.Discounts[row?.Discounts.length - 1].Id + 1 : Id,
                        ParentId: row.Id,
                        MaxVolume: null,
                        MinVolume: null,
                        Percent: null,
                        Cells: null
                    }
                    row?.Discounts.push(discount);
                }
            }
        },
        deleteDiscountList(state: PriceSettingsState, data: PriceSettings) {
            data.Discounts = [];
        },
        setRowExpandedState(state: PriceSettingsState, { row, typeCode }: ToggleModel) {
            
            const rows = typeCode == 'delivery' ? state.deliveryPriceSettings : state.pickupPriceSettings;

            if(rows) {
                setRowExpandedState(rows, row);
            }
            
            if(state.keyword) {
                store.dispatch('searchDelivery', state.keyword)
            }
        },
        setRowCheckingState(state: PriceSettingsState, { row, inputValue, typeCode }: CheckModel) {
            const rows = typeCode == 'delivery' ? state.deliveryPriceSettings : state.pickupPriceSettings;

            if(rows) {
                setRowCheckingState(rows, row, inputValue);
            }
        },
        setDeliveryPriceSettings(state: PriceSettingsState, rootList: DeliveryPriceSettings[]) {
            state.deliveryPriceSettings = rootList.map((item: DeliveryPriceSettings) => setPriceSettings(item, rootList));
        },
        setPickupPriceSettings(state: PriceSettingsState, rootList: PickupPriceSettings[]) {
            state.pickupPriceSettings = rootList.map((item: PickupPriceSettings) => setPriceSettings(item, rootList));
        },
        setUnitName(state: PriceSettingsState,unitName: string) {
            state.unitName = unitName;
        }
    },
    getters: {
        getDeliveryPriceSettingsForTable(state: PriceSettingsState) {
            
            const list = state.deliveryPriceSettings?.map((item) => {
                item.Cells = getDeliveryCells(item, deliveryCellSettings);

                return item;
            }).filter((item) => item.Hidden == false);

            return {
                list: list,
                columnsHeaders: Object.values(deliveryCellSettings) 
            }
        },
        getPickupPriceSettingsForTable(state: PriceSettingsState) {
            
            const list = state.pickupPriceSettings?.map((item) => {
                item.Cells = getPickupCells(item, pickupCellSettings);

                return item;
            }).filter((item) => item.Hidden == false);

            return {
                list: list,
                columnsHeaders: Object.values(pickupCellSettings) 
            }
        },
        getUnitName(state: PriceSettingsState) {
            return state.unitName;
        }
    }
}